import type { ReactElement } from 'react';
import { createBrowserRouter, redirect, type RouteObject } from 'react-router';

import { BaseUrlRoute, PersonsRoute } from '@/config/router/routes';
import { requireTabletSession, tabletLoginRedirect } from '@/config/router/sessionGuard';
import { AiMessageSourcePage } from '@/presentation/pages/aiMessageSource/AiMessageSourcePage';
import { BaseUrlPage } from '@/presentation/pages/baseUrl/BaseUrlPage';
import { ChatPage } from '@/presentation/pages/chat/ChatPage';
import { DiagnosticsPage } from '@/presentation/pages/diagnostics/DiagnosticsPage';
import { LanguagePage } from '@/presentation/pages/language/LanguagePage';
import { PersonsPage } from '@/presentation/pages/persons/PersonsPage';
import { TabletLoginPage } from '@/presentation/pages/tabletLogin/TabletLoginPage';
import { VerifyAdminPage } from '@/presentation/pages/verifyAdmin/VerifyAdminPage';
import { RouteFadeOutlet } from '@/presentation/widgets/routePages/RouteFade';
import { TransparentOverlay } from '@/presentation/widgets/routePages/TransparentOverlay';
import { StorageHelper } from '@/utils/storageHelper';

/**
 * Перенос `lib/src/config/router/router.dart` и дерева `$appRoutes`
 * из `routes.g.dart`.
 *
 * В Dart роутер жил в `AutoDisposeProvider` и умирал вместе с ним; здесь
 * его собирает [createAppRouter] один раз за жизнь `App` — `MainApp` и так
 * создавался единожды, а `ref.onDispose(router.dispose)` срабатывал только
 * при перезапуске приложения.
 *
 * Не на импорте модуля: `createBrowserRouter` сразу запускает загрузчики
 * маршрутов, а они читают хранилище — адрес планшета и его сессию. Туда их
 * кладёт `bootstrapApp`, который отрабатывает позже импорта.
 *
 * `rootNavigatorKey` не переносится: ключ навигатора нужен был go_router
 * для вложенных `ShellRoute`, которых в приложении нет, и ни одна страница
 * к нему не обращалась. `debugLogDiagnostics: true` — тоже: это вывод
 * go_router в консоль, в вебе его роль играет журнал разработчика.
 *
 * Маршрут `installed-apps` убран вместе с экраном списка приложений.
 */

/** `Colors.transparent` у `TransparentPage` страницы источника. */
const SOURCE_BARRIER_COLOR = 'transparent';

/**
 * `AiMessageSourceRoute.buildPage` — прозрачная страница поверх чата.
 *
 * Маршрут вложен в чат, поэтому чат остаётся смонтированным и продолжает
 * рисоваться под оверлеем — ровно как `opaque: false` во Flutter.
 */
function AiMessageSourceOverlay(): ReactElement {
  return (
    <TransparentOverlay backgroundColor={SOURCE_BARRIER_COLOR}>
      <AiMessageSourcePage />
    </TransparentOverlay>
  );
}

/**
 * `initialLocation` из `router.dart`.
 *
 * Отличие от Dart: начальный адрес в браузере задаёт строка адреса, а не
 * роутер, поэтому выбор делается перенаправлением с `/` — единственной
 * точки, куда киоск приходит при обычном запуске.
 *
 * Второе отличие — сам экран. В Dart киоск открывался выбором языка, а
 * планшет и аватара выбирал администратор при установке. Здесь планшет
 * вшит в сборку (`VITE_DEFAULT_BASE_URL`), поэтому первым посетитель
 * видит список аватаров: выбрал собеседника — выбрал язык — говоришь.
 *
 * Выход по PIN по-прежнему ведёт на экран адреса: этот флаг ставит
 * администратор, и ему нужен именно он.
 */
function initialLocation(): string {
  return StorageHelper.instance.askBaseUrlAfterExit
    ? BaseUrlRoute.location()
    : PersonsRoute.location;
}

function redirectToInitialLocation(): Response {
  return redirect(initialLocation());
}

const routes: RouteObject[] = [
  {
    // Корневая раскладка без адреса: она же площадка затухания между
    // экранами. В Dart её место занимал `RootRoute` с пустым `SizedBox()`.
    element: <RouteFadeOutlet />,
    children: [
      { index: true, loader: redirectToInitialLocation },
      // Экраны посетителя открываются только с сессией планшета
      // (`requireTabletSession`); без неё — экран пароля, как в APK.
      { path: 'tablet-login', element: <TabletLoginPage />, loader: tabletLoginRedirect },
      { path: 'select-language', element: <LanguagePage />, loader: requireTabletSession },
      { path: 'select-language/:verifyAdminType/baseUrl', element: <BaseUrlPage /> },
      { path: 'select-language/:type/verify-admin', element: <VerifyAdminPage /> },
      {
        path: 'select-language/persons',
        element: <PersonsPage />,
        loader: requireTabletSession,
      },
      { path: 'select-language/diagnostics', element: <DiagnosticsPage /> },
      {
        path: 'select-language/:lang/chat/:personId',
        element: <ChatPage />,
        loader: requireTabletSession,
        children: [{ path: 'ai-message-source', element: <AiMessageSourceOverlay /> }],
      },
      // Неизвестный адрес: в go_router его не бывало (все переходы шли
      // через типизированные маршруты), а в браузере его можно набрать
      // руками. Киоску нечего показать на странице ошибки, поэтому он
      // уходит туда же, куда и при запуске.
      { path: '*', loader: redirectToInitialLocation },
    ],
  },
];

/** Роутер приложения. Зовётся после `bootstrapApp` — см. комментарий выше. */
export function createAppRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(routes);
}
